package top.wcpe.allincore.ui.runtime.ir.request

import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.state.StateMachineExecutor
import kotlin.jvm.JvmStatic
import kotlin.jvm.JvmSuppressWildcards

/**
 * Request 回调投递工具：网络层收到 UiCallback / 触发 timeout 时调用。
 *
 * 命中后会从 [RequestRegistry] 移除登记项；按 [PendingRequest.isStateMachine]
 * 分流执行：
 * - 状态机形态（D2-b 主路径）：把 success/fail/timeout 投递到 transition 指定的下一状态号，
 *   record 载着快照继续推进；bindVarName 与 tailExpr 由本类负责注入。
 * - 旧 callback-body 形态：保留兼容，按 success/fail/timeout 三段 body + paramName 投递。
 */
object RequestCallbacks {

    /** 投递 success/fail 回包；payload 为协议层已解码的扁平 KV 对象。 */
    @JvmStatic
    fun deliver(
        registry: RequestRegistry,
        requestId: String,
        success: Boolean,
        payload: Map<String, @JvmSuppressWildcards Any>,
        baseCtx: EvalContext,
    ) {
        val pr = registry.take(requestId) ?: return
        val kindName = if (success) "success" else "failure"
        if (pr.stateMachine) {
            val successId = if (success) pr.successNextStateId else pr.failureNextStateId
            resumeStateMachine(pr, kindName, payload, success, successId, baseCtx)
            return
        }
        // ---- 旧 callback-body 路径 ----
        val body = if (success) pr.successBody else pr.failBody
        val param = if (success) pr.successParam else pr.failParam
        if (body.isNullOrEmpty()) return
        val locals = buildLocals(param, payload, pr.netMode, kindName)
        ExprEvaluator.execute(body, baseCtx.withLocals(locals), pr.capturedVars)
    }

    /** 触发 timeout 回调（旧块语法无参；net/状态机形态注入 {kind: timeout, payload: {}}）。 */
    @JvmStatic
    fun deliverTimeout(registry: RequestRegistry, requestId: String, baseCtx: EvalContext) {
        val pr = registry.take(requestId) ?: return
        if (pr.stateMachine) {
            resumeStateMachine(pr, "timeout", null, false, pr.timeoutNextStateId, baseCtx)
            return
        }
        val body = pr.timeoutBody
        if (body.isNullOrEmpty()) return
        val locals = buildLocals(pr.timeoutParam, null, pr.netMode, "timeout")
        ExprEvaluator.execute(body, baseCtx.withLocals(locals), pr.capturedVars)
    }

    /**
     * 状态机形态：注入 __result（kind/payload 合成对象） + 把 bindVarName 写到 record，
     * success 路径下若有 tailExpr 则用 tailExpr 求值结果代替整 result。
     */
    private fun resumeStateMachine(
        pr: PendingRequest,
        kind: String,
        payload: Map<String, @JvmSuppressWildcards Any>?,
        isSuccess: Boolean,
        nextStateId: Int,
        baseCtx: EvalContext,
    ) {
        val record = pr.record ?: return
        val machine = pr.machine ?: return
        val resultObj = RequestExecutor.buildNetResult(kind, payload)
        val ctxWithResult = baseCtx.withLocals(mapOf("__result" to resultObj))
        if (pr.bindVarName != null) {
            // tailExpr 求值结果可为 null（DSL 表达式可求值为 null），保持可空与旧 Java 路径一致
            val bound: Any?
            val tail = pr.tailExpr
            bound = if (isSuccess && tail != null) {
                ExprEvaluator.eval(tail, ctxWithResult)
            } else {
                resultObj
            }
            record.set(pr.bindVarName, bound)
        }
        try {
            StateMachineExecutor.executeFrom(machine, nextStateId, record, ctxWithResult)
        } catch (rs: ExprEvaluator.ReturnSignal) {
            // 状态机内部 return：在 callable 边界吞掉
        }
    }

    /** 旧路径：组装回调局部 frame。 */
    private fun buildLocals(
        param: String?,
        payload: Map<String, @JvmSuppressWildcards Any>?,
        netMode: Boolean,
        kind: String,
    ): Map<String, Any> {
        if (param == null) return emptyMap()
        return if (netMode) {
            mapOf(param to RequestExecutor.buildNetResult(kind, payload))
        } else {
            val copy: Map<String, Any> = if (payload.isNullOrEmpty()) linkedMapOf() else LinkedHashMap(payload)
            mapOf(param to copy)
        }
    }
}
